// Paste to create a Lead (staff) — a self-contained two-phase modal. Phase 1 is
// a large paste box that harvests every hyperlink from a rich clipboard payload;
// Extract runs the AI endpoint. Phase 2 shows the full New-Lead field set
// PRE-FILLED for the reviewer to correct, with a non-blocking duplicate warning,
// then Save creates the lead. Nothing auto-creates.

import { useEffect, useState, type ClipboardEvent } from "react";
import { ClipboardPaste, Save, Sparkles, X } from "lucide-react";
import { Button, FormField, Input, Textarea } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { ApiError, extractErrorMessage } from "@/api/client";
import {
  leadsApi,
  type LeadDuplicateMatchDto,
  type LeadExtractionResult,
  type LeadSource,
  type UpsertBrokerLeadPayload,
} from "@/api/leads";
import { US_STATES } from "@/lib/usStates";
import { emailFieldError } from "@/lib/emailValidator";
import { phoneFieldError } from "@/lib/phoneValidator";
import { extractLinks, mergeLinks, type CapturedLink } from "@/lib/clipboardLinks";

interface PasteToCreateLeadModalProps {
  onClose: () => void;
  onCreated: () => void;
}

// All 12 LeadSource values (mirrors LeadEditPage's SOURCES).
const SOURCES: LeadSource[] = [
  "ColdCall", "InboundInquiry", "TradeShow", "Referral", "WebForm",
  "SocialMedia", "IBBA", "MaSource", "BizBuySell", "LinkedIn",
  "PurchasedList", "Other",
];

// BrokerLead.Notes is nvarchar(4000).
const NOTES_MAX = 4000;

// Rich-paste link capture lives in @/lib/clipboardLinks, shared with the Lead
// Generation PreleadsTab. This surface keeps EVERY link it finds — the
// boilerplate denylist is opt-in and only the portal paste box uses it.

// ── Review form ─────────────────────────────────────────────────────────────

interface ReviewForm {
  firstName: string;
  lastName: string;
  companyName: string;
  title: string;
  phone: string;
  email: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  source: LeadSource;
  sourceDetail: string;
  notes: string;
}

const EMPTY_REVIEW: ReviewForm = {
  firstName: "", lastName: "", companyName: "", title: "",
  phone: "", email: "", address: "", city: "", state: "",
  zip: "", source: "Other", sourceDetail: "", notes: "",
};

/** True when the extraction identified nobody — no name, no email, no phone.
 *
 *  Deliberately NOT a server 422: the 422 branch below seeds the form with
 *  `buildNotes(null, links)`, which DISCARDS the AI summary. Raising this case
 *  server-side would trade a silently blank form for a loud form that also
 *  destroys the one field that did come back. So the contract stays 200 with
 *  however sparse a DTO the model produced, the summary still lands in Notes,
 *  and the reviewer is simply told what happened.
 *
 *  notesSummary is not part of the test — it is what we still have to show, not
 *  evidence that a contact was found. */
function foundNoContact(r: LeadExtractionResult): boolean {
  return !r.firstName?.trim()
    && !r.lastName?.trim()
    && !r.email?.trim()
    && !r.phone?.trim();
}

/** notesSummary + (deduped) captured link hrefs, capped to the column width. */
function buildNotes(summary: string | null | undefined, links: CapturedLink[]): string {
  let notes = (summary ?? "").trim();
  if (links.length > 0) {
    const hrefs = Array.from(new Set(links.map((l) => l.href)));
    notes = (notes ? `${notes}\n\n` : "") + `Links:\n${hrefs.join("\n")}`;
  }
  return notes.slice(0, NOTES_MAX);
}

/** Paste to create a Lead — AI-assisted single-modal lead creation. */
export function PasteToCreateLeadModal({ onClose, onCreated }: PasteToCreateLeadModalProps) {
  // Phase 1 — paste.
  const [text, setText] = useState("");
  const [links, setLinks] = useState<CapturedLink[]>([]);
  const [extracting, setExtracting] = useState(false);
  const [extractError, setExtractError] = useState<string | null>(null);
  const [fallbackNote, setFallbackNote] = useState<string | null>(null);

  // Phase 2 — review.
  const [reviewing, setReviewing] = useState(false);
  const [form, setForm] = useState<ReviewForm>(EMPTY_REVIEW);
  const [modelUsed, setModelUsed] = useState<string | null>(null);
  const [dupes, setDupes] = useState<LeadDuplicateMatchDto[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const set = <K extends keyof ReviewForm>(key: K, val: ReviewForm[K]) =>
    setForm((f) => ({ ...f, [key]: val }));

  const emailError = emailFieldError(form.email, { required: false });
  const phoneError = phoneFieldError(form.phone, { required: false });
  const canSave = !!form.firstName.trim() && !!form.lastName.trim() && !emailError && !phoneError;

  const handlePaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const html = e.clipboardData.getData("text/html");
    const plain = e.clipboardData.getData("text/plain");
    const found = extractLinks(html, plain);
    if (found.length > 0) setLinks((prev) => mergeLinks(prev, found));
  };

  const extract = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setExtracting(true);
    setExtractError(null);
    setFallbackNote(null);
    try {
      const result = await leadsApi.extractFromText(trimmed);
      setForm({
        firstName: result.firstName ?? "",
        lastName: result.lastName ?? "",
        companyName: result.companyName ?? "",
        title: result.title ?? "",
        phone: result.phone ?? "",
        email: result.email ?? "",
        address: result.address ?? "",
        city: result.city ?? "",
        state: result.state ?? "",
        zip: result.zip ?? "",
        source: result.source,
        sourceDetail: "",
        notes: buildNotes(result.notesSummary, links),
      });
      setModelUsed(result.modelUsed);
      if (foundNoContact(result)) {
        setFallbackNote(
          "We couldn't identify a contact in that text — everything we found is in Notes below.",
        );
      }
      setReviewing(true);
    } catch (err) {
      // A 422 means "we can't extract this one" — keep the pasted text and drop
      // the reviewer into an empty form to fill in manually (captured links are
      // still seeded into notes). Any other error stays on the paste step.
      //
      // Render the SERVER's message: a 422 is raised both when the AI couldn't
      // read the text AND when the daily AI cost ceiling is reached, and those
      // read completely differently to the person waiting on it. Falling back to
      // fixed copy only when the server sent none.
      if (err instanceof ApiError && err.status === 422) {
        setForm({ ...EMPTY_REVIEW, notes: buildNotes(null, links) });
        setModelUsed(null);
        setFallbackNote(
          err.message.trim() ||
            "We couldn't read that automatically — add the details below; your pasted text is kept.",
        );
        setReviewing(true);
      } else {
        setExtractError(extractErrorMessage(err));
      }
    } finally {
      setExtracting(false);
    }
  };

  // Non-blocking, debounced duplicate check keyed on phone/email/company. Swallow
  // failures — the warning is advisory and never gates Save.
  useEffect(() => {
    if (!reviewing) return;
    const phone = form.phone.trim();
    const email = form.email.trim();
    const company = form.companyName.trim();
    if (!phone && !email && !company) {
      setDupes([]);
      return;
    }
    let cancelled = false;
    const t = window.setTimeout(() => {
      leadsApi
        .duplicates(phone || undefined, email || undefined, company || undefined)
        .then((rows) => {
          if (!cancelled) setDupes(rows);
        })
        .catch(() => {
          if (!cancelled) setDupes([]);
        });
    }, 400);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [reviewing, form.phone, form.email, form.companyName]);

  const save = async () => {
    setSaveError(null);
    if (!form.firstName.trim() || !form.lastName.trim()) {
      setSaveError("First and last name are required.");
      return;
    }
    if (emailError || phoneError) {
      setSaveError(emailError ?? phoneError ?? null);
      return;
    }
    setSaving(true);
    try {
      const payload: UpsertBrokerLeadPayload = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        companyName: form.companyName.trim() || null,
        title: form.title.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        address: form.address.trim() || null,
        city: form.city.trim() || null,
        state: form.state || null,
        zip: form.zip.trim() || null,
        source: form.source,
        sourceDetail: form.sourceDetail.trim() || null,
        interestLevel: "Unknown",
        notes: form.notes.trim() || null,
        rawPastedText: text.trim() || null,
        aiModel: modelUsed ?? null,
      };
      await leadsApi.create(payload);
      onCreated();
      onClose();
    } catch (err) {
      setSaveError(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Paste to create lead"
      className="fixed inset-0 z-40 bg-onyx/50 flex items-center justify-center p-space-4"
    >
      <div
        className="bg-white-pure rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-space-5 border-t-[2px] border-t-gold"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-space-3">
          <div>
            <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow mb-space-1">
              Lead · paste to create
            </div>
            <h2 className="font-display font-bold text-h3 text-onyx m-0">
              {reviewing ? "Review & save lead" : "New lead"}
            </h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="text-gray-mid hover:text-onyx">
            <X size={18} strokeWidth={1.5} aria-hidden />
          </button>
        </div>

        {!reviewing ? (
          <div className="grid gap-space-3">
            <p className="text-small text-gray-mid m-0">
              Paste a broker profile, an email signature, a broker email — or a
              listing — and we&apos;ll pull out their contact details for you to
              review before saving. Links are captured automatically.
            </p>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onPaste={handlePaste}
              rows={9}
              placeholder="Paste here…"
              aria-label="Paste text to extract a lead from"
              autoFocus
            />
            {links.length > 0 && (
              <p className="text-caption text-gray-mid m-0">
                Captured {links.length} link{links.length === 1 ? "" : "s"} — they&apos;ll be added to the notes.
              </p>
            )}
            {extractError && (
              <p className="text-caption text-status-error m-0" role="alert">
                {extractError}
              </p>
            )}
          </div>
        ) : (
          <div className="grid gap-space-3">
            {fallbackNote && (
              <div className="rounded-md border border-gold/40 bg-gold/10 px-space-3 py-space-2 text-caption text-onyx" role="status">
                {fallbackNote}
              </div>
            )}

            {dupes.length > 0 && <DuplicateWarning matches={dupes} />}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-3">
              <FormField label="First name" required>
                <Input value={form.firstName} autoFocus onChange={(e) => set("firstName", e.target.value)} />
              </FormField>
              <FormField label="Last name" required>
                <Input value={form.lastName} onChange={(e) => set("lastName", e.target.value)} />
              </FormField>
              <FormField label="Email" error={emailError}>
                <Input type="email" value={form.email} invalid={!!emailError} onChange={(e) => set("email", e.target.value)} />
              </FormField>
              <FormField label="Phone" error={phoneError}>
                <Input type="tel" value={form.phone} invalid={!!phoneError} onChange={(e) => set("phone", e.target.value)} />
              </FormField>
              <FormField label="Company">
                <Input value={form.companyName} onChange={(e) => set("companyName", e.target.value)} />
              </FormField>
              <FormField label="Title">
                <Input value={form.title} onChange={(e) => set("title", e.target.value)} />
              </FormField>
              <FormField label="Address" className="sm:col-span-2">
                <Input value={form.address} onChange={(e) => set("address", e.target.value)} />
              </FormField>
              <FormField label="City">
                <Input value={form.city} onChange={(e) => set("city", e.target.value)} />
              </FormField>
              <FormField label="State">
                <Select value={form.state} onChange={(e) => set("state", e.target.value)}>
                  <option value="">—</option>
                  {US_STATES.map((s) => <option key={s.code} value={s.code}>{s.name} ({s.code})</option>)}
                </Select>
              </FormField>
              <FormField label="ZIP">
                <Input value={form.zip} onChange={(e) => set("zip", e.target.value)} />
              </FormField>
              <FormField label="Source">
                <Select value={form.source} onChange={(e) => set("source", e.target.value as LeadSource)}>
                  {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
                </Select>
              </FormField>
              <FormField label="Source detail" className="sm:col-span-2">
                <Input value={form.sourceDetail} onChange={(e) => set("sourceDetail", e.target.value)} />
              </FormField>
            </div>

            <FormField label="Notes">
              <Textarea value={form.notes} rows={5} onChange={(e) => set("notes", e.target.value)} />
            </FormField>

            {saveError && (
              <p className="text-caption text-status-error m-0" role="alert">
                {saveError}
              </p>
            )}
          </div>
        )}

        <div className="flex items-center justify-end gap-space-2 mt-space-4">
          <Button variant="text" size="sm" onClick={onClose}>Cancel</Button>
          {!reviewing ? (
            <Button
              size="sm"
              disabled={extracting || text.trim().length === 0}
              onClick={extract}
            >
              <Sparkles size={14} strokeWidth={1.5} aria-hidden /> {extracting ? "Extracting…" : "Extract"}
            </Button>
          ) : (
            <Button size="sm" disabled={saving || !canSave} onClick={save}>
              {saving ? (
                "Saving…"
              ) : (
                <>
                  <Save size={14} strokeWidth={1.5} aria-hidden /> {dupes.length > 0 ? "Save anyway" : "Save"}
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/** Non-blocking possible-duplicate notice shown in the review step. Each match
 *  links to the existing lead/broker in a NEW TAB so the modal isn't lost. */
function DuplicateWarning({ matches }: { matches: LeadDuplicateMatchDto[] }) {
  return (
    <div
      className="rounded-md border border-gold/40 bg-gold/10 px-space-3 py-space-2"
      role="status"
    >
      <p className="flex items-center gap-space-1 text-small font-semibold text-onyx m-0">
        <ClipboardPaste size={14} className="text-gold-shadow" aria-hidden />
        Possible duplicate{matches.length > 1 ? "s" : ""} — you can still save:
      </p>
      <ul className="list-none p-0 m-0 mt-space-1 space-y-space-1">
        {matches.map((m, i) => {
          const href = m.leadId ? `/leads/${m.leadId}` : m.brokerId ? `/brokers/${m.brokerId}` : null;
          return (
            <li key={`${m.leadId ?? m.brokerId ?? m.name}-${i}`} className="flex flex-wrap items-center gap-space-2 text-caption text-onyx">
              <span className="font-semibold">{m.name}</span>
              <span className="text-gray-mid">matched on {m.matchType}</span>
              {m.status && <span className="text-gray-mid">· {m.status}</span>}
              {href && (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-gold-shadow hover:underline"
                >
                  Open existing
                </a>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
